import { useState, useRef, useEffect } from "react";
import { renderFormula } from "@/lib/chemicalFormula";
import type { Chemical, StoichiometryUnit } from "@/types/chemistry";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, X, Copy } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { COMMON_REAGENTS } from "@/data/commonReagents";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type StoichiometryMode = "reactant" | "reagent" | "none";

interface ChemicalTableProps {
  title: string;
  chemicals: Chemical[];
  dateLabel: string;
  onAdd: (data?: Partial<Chemical>) => void;
  onUpdate: (id: string, updates: Partial<Chemical>) => void;
  onRemove: (id: string) => void;
  showAdd?: boolean;
  showCo2?: boolean;
  stoichiometryMode?: StoichiometryMode;
  nameEditable?: boolean;
}

const FetchingCell = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Skeleton className="h-4 w-16 rounded" />
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-xs">Data is being fetched — please refresh in a few minutes</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const CopyableCell = ({ value, placeholder = "—", onChange }: { value: string; placeholder?: string; onChange: (v: string) => void; id: string; field: string }) => {
  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="flex items-center gap-1 group/copy overflow-hidden w-full">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-7 text-xs border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 min-w-0"
        placeholder={placeholder}
      />
      {value && (
        <button
          onClick={handleCopy}
          className="opacity-0 group-hover/copy:opacity-100 transition-opacity text-muted-foreground hover:text-foreground shrink-0"
        >
          <Copy className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

/** Editable formula cell: shows subscripted formula, switches to input on click */
const FormulaCell = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  if (editing) {
    return (
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => e.key === "Enter" && setEditing(false)}
        className="h-7 text-xs border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
        placeholder="—"
      />
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="h-7 text-xs text-left w-full cursor-text"
    >
      {value ? <span className="font-mono">{renderFormula(value)}</span> : <span className="text-muted-foreground">—</span>}
    </button>
  );
};

const REAGENT_UNITS: { value: StoichiometryUnit; label: string; hint: string }[] = [
  { value: "equiv.", label: "equiv.", hint: "mol substance / mol limiting reagent" },
  { value: "M", label: "M", hint: "mol / L solvent" },
  { value: "mol%", label: "mol %", hint: "% of limiting reagent in mol" },
  { value: "wt%", label: "wt %", hint: "% of reaction mixture weight" },
];

/** Autocomplete input for searching reagent names */
const ReagentNameSearch = ({
  onSelect,
}: {
  onSelect: (reagent: { name: string; formula?: string; cas?: string }) => void;
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const filtered = query.length >= 1
    ? COMMON_REAGENTS.filter((r) =>
        r.name.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 8)
    : COMMON_REAGENTS.slice(0, 8);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <Input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="+ New entry"
        className={cn(
          "h-7 text-xs border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 placeholder:text-muted-foreground transition-colors",
          open ? "text-foreground placeholder:text-foreground/70" : "text-muted-foreground"
        )}
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 top-full left-0 mt-1 w-64 bg-popover border rounded-md shadow-lg max-h-48 overflow-y-auto">
          {filtered.map((r) => (
            <button
              key={r.cas || r.name}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors flex items-center justify-between"
              onClick={() => {
                onSelect(r);
                setQuery("");
                setOpen(false);
              }}
            >
              <span className="font-medium">{r.name}</span>
              {r.formula && <span className="text-muted-foreground ml-2">{renderFormula(r.formula)}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ChemicalTable = ({
  title,
  chemicals,
  dateLabel,
  onAdd,
  onUpdate,
  onRemove,
  showAdd = true,
  showCo2 = true,
  stoichiometryMode = "none",
  nameEditable = true,
}: ChemicalTableProps) => {
  const showStoichiometry = stoichiometryMode !== "none";
  const isReagentMode = stoichiometryMode === "reagent";

  const stoichHeader = isReagentMode ? "Stoichiometry" : "Stoich. (mol/mol)";

  return (
    <div>
      <h3 className="text-sm font-semibold text-primary mb-2">{title}</h3>
      <div className="overflow-x-auto">
        <table className="text-xs w-full">
          <thead>
            <tr className="border-b text-muted-foreground">
              <th className="text-left font-medium py-1.5 pr-3 whitespace-nowrap">Name</th>
              <th className="text-left font-medium py-1.5 pr-3 whitespace-nowrap">Chemical Formula</th>
              <th className="text-left font-medium py-1.5 pr-3 whitespace-nowrap">CAS #</th>
              <th className="text-left font-medium py-1.5 pr-3 whitespace-nowrap">MW (g/mol)</th>
              {showStoichiometry && (
                <th className="text-left font-medium py-1.5 pr-3 whitespace-nowrap">{stoichHeader}</th>
              )}
              <th className="text-left font-medium py-1.5 pr-3 whitespace-nowrap">{dateLabel}</th>
              <th className="text-left font-medium py-1.5 pr-3 whitespace-nowrap">Price €/mol</th>
              {showCo2 && <th className="text-left font-medium py-1.5 pr-3 whitespace-nowrap">CO₂eq kg/mol</th>}
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {chemicals.map((chem) => (
              <tr key={chem.id} className="border-b border-border/50 group">
                {/* Name */}
                <td className="py-1 pr-3">
                  {nameEditable ? (
                    <Input
                      value={chem.name || ""}
                      onChange={(e) => onUpdate(chem.id, { name: e.target.value })}
                      className="h-7 text-xs border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 overflow-hidden text-ellipsis"
                      placeholder="—"
                    />
                  ) : (
                    <span className={cn("text-xs", !chem.name && "text-muted-foreground/50 italic")}>
                      {chem.name || "—"}
                    </span>
                  )}
                </td>
                <td className="py-1 pr-3">
                  <FormulaCell
                    value={chem.formula}
                    onChange={(v) => onUpdate(chem.id, { formula: v })}
                  />
                </td>
                <td className="py-1 pr-3">
                  <CopyableCell
                    value={chem.casNumber}
                    onChange={(v) => onUpdate(chem.id, { casNumber: v })}
                    id={chem.id}
                    field="casNumber"
                  />
                </td>
                <td className="py-1 pr-3">
                  <CopyableCell
                    value={chem.molarWeight != null ? String(chem.molarWeight) : ""}
                    onChange={(v) => onUpdate(chem.id, { molarWeight: parseFloat(v) || undefined })}
                    id={chem.id}
                    field="molarWeight"
                  />
                </td>
                {/* Stoichiometry */}
                {showStoichiometry && (
                  <td className="py-1 pr-3">
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        value={chem.stoichiometry ?? ""}
                        onChange={(e) =>
                          onUpdate(chem.id, {
                            stoichiometry: e.target.value ? parseFloat(e.target.value) : undefined,
                          })
                        }
                        className="h-7 text-xs border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 w-16"
                        placeholder="—"
                      />
                      {isReagentMode && (
                        <Select
                          value={chem.stoichiometryUnit || "equiv."}
                          onValueChange={(v) =>
                            onUpdate(chem.id, { stoichiometryUnit: v as StoichiometryUnit })
                          }
                        >
                          <SelectTrigger className="h-7 text-[10px] border-0 bg-transparent shadow-none px-1 w-[70px] gap-0.5 focus:ring-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {REAGENT_UNITS.map((u) => (
                              <SelectItem key={u.value} value={u.value} className="text-xs">
                                <span title={u.hint}>{u.label}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </td>
                )}
                <td className="py-1 pr-3">
                  {!chem.earliestDate ? (
                    <FetchingCell />
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          className={cn(
                            "flex items-center gap-1 h-7 text-xs bg-transparent px-0 hover:text-primary transition-colors",
                            !chem.earliestDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="h-3 w-3 shrink-0" />
                          {format(parseISO(chem.earliestDate), "MMM d, yyyy")}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={parseISO(chem.earliestDate)}
                          onSelect={(d) => onUpdate(chem.id, { earliestDate: d ? format(d, "yyyy-MM-dd") : "" })}
                          initialFocus
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                </td>
                <td className="py-1 pr-3">
                  {!chem.price ? (
                    <FetchingCell />
                  ) : (
                    <Input
                      value={chem.price || ""}
                      onChange={(e) => onUpdate(chem.id, { price: parseFloat(e.target.value) || 0 })}
                      className="h-7 text-xs border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
                      placeholder="—"
                    />
                  )}
                </td>
                {showCo2 && (
                  <td className="py-1 pr-3">
                    <Input
                      value={chem.co2eq || ""}
                      onChange={(e) => onUpdate(chem.id, { co2eq: parseFloat(e.target.value) || 0 })}
                      className="h-7 text-xs border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
                      placeholder="—"
                    />
                  </td>
                )}
                <td className="py-1 sticky right-0 bg-background">
                  <button
                    onClick={() => onRemove(chem.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Add row */}
      {showAdd && isReagentMode && (
        <div className="mt-1.5">
          <ReagentNameSearch
            onSelect={(reagent) => {
              onAdd({
                name: reagent.name,
                formula: reagent.formula || "",
                casNumber: reagent.cas || "",
                stoichiometryUnit: "equiv.",
              });
            }}
          />
        </div>
      )}
      {showAdd && !isReagentMode && (
        <button
          onClick={() => onAdd()}
          className="mt-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          + New entry
        </button>
      )}
    </div>
  );
};
